// =====================================================
// SERVICE: Système de Coupons Unifié
// Date: 2025-11-28
// Description: Gestion des coupons pour shop, elearning et psy
// =====================================================

import { supabase } from '../lib/supabase';
import { applyDomainFilterToQuery, getDomainFilterParams } from '../hooks/useDomainQuery';

// =====================================================
// TYPES
// =====================================================

export type ModuleType = 'shop' | 'elearning' | 'psy' | 'dating';
export type DiscountType = 'percentage' | 'fixed_amount';

export interface Coupon {
  id: string;
  code: string;
  name: string;
  description: string | null;
  discount_type: DiscountType;
  discount_value: number;
  min_purchase_amount: number;
  max_discount_amount: number | null;
  usage_limit: number | null;
  usage_limit_per_user: number;
  usage_count: number;
  valid_from: string;
  valid_until: string;
  is_active: boolean;
  applies_to_modules: ModuleType[];
  tenant_id: number;
  domain: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface CouponUsage {
  id: string;
  coupon_id: string;
  user_email: string;
  user_id: string | null;
  module_type: ModuleType;
  order_id: string | null;
  original_amount: number;
  discount_amount: number;
  final_amount: number;
  tenant_id: number;
  domain: string | null;
  created_at: string;
}

export interface CouponValidationResult {
  valid: boolean;
  coupon?: Coupon;
  discount_amount?: number;
  final_amount?: number;
  error?: string;
  error_code?: 'NOT_FOUND' | 'INACTIVE' | 'EXPIRED' | 'NOT_STARTED' | 'MODULE_NOT_ALLOWED' | 
               'MIN_AMOUNT_NOT_MET' | 'USAGE_LIMIT_REACHED' | 'USER_LIMIT_REACHED';
}

export interface ApplyCouponParams {
  coupon_code: string;
  module_type: ModuleType;
  order_id: string;
  user_email: string;
  user_id?: string;
  original_amount: number;
  discount_amount: number;
  final_amount: number;
}

// =====================================================
// FONCTIONS DE VALIDATION
// =====================================================

/**
 * Valide un coupon pour un module et un montant donnés
 */
export const validateCoupon = async (
  code: string,
  module: ModuleType,
  amount: number,
  userEmail: string
): Promise<CouponValidationResult> => {
  try {
    const { domain, tenantId } = getDomainFilterParams();
    
    // 1. Récupérer le coupon par code
    let query = supabase
      .from('coupons')
      .select('*')
      .eq('code', code.toUpperCase().trim());
    
    query = applyDomainFilterToQuery(query);
    
    const { data: coupon, error } = await query.maybeSingle();
    
    if (error) {
      console.error('Erreur lors de la recherche du coupon:', error);
      throw error;
    }
    
    // 2. Vérifier si le coupon existe
    if (!coupon) {
      return {
        valid: false,
        error: 'Code promo invalide',
        error_code: 'NOT_FOUND'
      };
    }
    
    // 3. Vérifier si le coupon est actif
    if (!coupon.is_active) {
      return {
        valid: false,
        error: 'Ce code promo n\'est plus actif',
        error_code: 'INACTIVE'
      };
    }
    
    // 4. Vérifier la période de validité
    const now = new Date();
    const validFrom = new Date(coupon.valid_from);
    const validUntil = new Date(coupon.valid_until);
    
    if (now < validFrom) {
      return {
        valid: false,
        error: `Ce code promo sera valide à partir du ${validFrom.toLocaleDateString('fr-FR')}`,
        error_code: 'NOT_STARTED'
      };
    }
    
    if (now > validUntil) {
      return {
        valid: false,
        error: 'Ce code promo a expiré',
        error_code: 'EXPIRED'
      };
    }
    
    // 5. Vérifier si le module est autorisé
    if (!coupon.applies_to_modules.includes(module)) {
      const moduleNames: Record<ModuleType, string> = {
        shop: 'la boutique',
        elearning: 'les formations',
        psy: 'les consultations psy',
        dating: 'les abonnements rencontres'
      };
      return {
        valid: false,
        error: `Ce code promo n'est pas valide pour ${moduleNames[module]}`,
        error_code: 'MODULE_NOT_ALLOWED'
      };
    }
    
    // 6. Vérifier le montant minimum
    if (coupon.min_purchase_amount && amount < coupon.min_purchase_amount) {
      return {
        valid: false,
        error: `Montant minimum requis: ${coupon.min_purchase_amount.toLocaleString('fr-FR')} FCFA`,
        error_code: 'MIN_AMOUNT_NOT_MET'
      };
    }
    
    // 7. Vérifier la limite globale d'utilisation
    if (coupon.usage_limit !== null && coupon.usage_count >= coupon.usage_limit) {
      return {
        valid: false,
        error: 'Ce code promo a atteint sa limite d\'utilisation',
        error_code: 'USAGE_LIMIT_REACHED'
      };
    }
    
    // 8. Vérifier la limite par utilisateur
    if (coupon.usage_limit_per_user > 0) {
      const userUsageCount = await getUserCouponUsageCount(coupon.id, userEmail);
      if (userUsageCount >= coupon.usage_limit_per_user) {
        return {
          valid: false,
          error: 'Vous avez déjà utilisé ce code promo',
          error_code: 'USER_LIMIT_REACHED'
        };
      }
    }
    
    // 9. Calculer la réduction
    const discountAmount = calculateDiscount(coupon, amount);
    const finalAmount = amount - discountAmount;
    
    return {
      valid: true,
      coupon: coupon as Coupon,
      discount_amount: discountAmount,
      final_amount: finalAmount
    };
    
  } catch (error) {
    console.error('Erreur lors de la validation du coupon:', error);
    return {
      valid: false,
      error: 'Erreur lors de la validation du code promo',
      error_code: 'NOT_FOUND'
    };
  }
};

/**
 * Calcule le montant de la réduction
 */
export const calculateDiscount = (coupon: Coupon, amount: number): number => {
  let discount = 0;
  
  if (coupon.discount_type === 'percentage') {
    discount = (amount * coupon.discount_value) / 100;
    
    // Appliquer le plafond si défini
    if (coupon.max_discount_amount !== null && discount > coupon.max_discount_amount) {
      discount = coupon.max_discount_amount;
    }
  } else {
    // Montant fixe
    discount = coupon.discount_value;
  }
  
  // La réduction ne peut pas dépasser le montant total
  return Math.min(discount, amount);
};

/**
 * Compte le nombre d'utilisations d'un coupon par un utilisateur
 */
export const getUserCouponUsageCount = async (
  couponId: string,
  userEmail: string
): Promise<number> => {
  const { domain, tenantId } = getDomainFilterParams();
  
  let query = supabase
    .from('coupon_usages')
    .select('id', { count: 'exact' })
    .eq('coupon_id', couponId)
    .eq('user_email', userEmail.toLowerCase().trim());
  
  query = applyDomainFilterToQuery(query);
  
  const { count, error } = await query;
  
  if (error) {
    console.error('Erreur lors du comptage des utilisations:', error);
    return 0;
  }
  
  return count || 0;
};

// =====================================================
// FONCTIONS D'APPLICATION
// =====================================================

/**
 * Applique un coupon et enregistre l'utilisation
 */
export const applyCoupon = async (params: ApplyCouponParams): Promise<CouponUsage> => {
  const { domain, tenantId } = getDomainFilterParams();
  
  // Récupérer le coupon
  let query = supabase
    .from('coupons')
    .select('id')
    .eq('code', params.coupon_code.toUpperCase().trim());
  
  query = applyDomainFilterToQuery(query);
  
  const { data: coupon, error: couponError } = await query.single();
  
  if (couponError || !coupon) {
    throw new Error('Coupon introuvable');
  }
  
  // Enregistrer l'utilisation
  const { data: usage, error: usageError } = await supabase
    .from('coupon_usages')
    .insert({
      coupon_id: coupon.id,
      user_email: params.user_email.toLowerCase().trim(),
      user_id: params.user_id || null,
      module_type: params.module_type,
      order_id: params.order_id,
      original_amount: params.original_amount,
      discount_amount: params.discount_amount,
      final_amount: params.final_amount,
      tenant_id: tenantId,
      domain: domain
    })
    .select()
    .single();
  
  if (usageError) {
    console.error('Erreur lors de l\'enregistrement de l\'utilisation:', usageError);
    throw usageError;
  }
  
  return usage as CouponUsage;
};

// =====================================================
// FONCTIONS CRUD (ADMIN)
// =====================================================

/**
 * Récupère tous les coupons (admin)
 */
export const getAllCoupons = async (
  filters?: {
    is_active?: boolean;
    module?: ModuleType;
  }
): Promise<Coupon[]> => {
  let query = supabase
    .from('coupons')
    .select('*')
    .order('created_at', { ascending: false });
  
  query = applyDomainFilterToQuery(query);
  
  if (filters?.is_active !== undefined) {
    query = query.eq('is_active', filters.is_active);
  }
  
  if (filters?.module) {
    query = query.contains('applies_to_modules', [filters.module]);
  }
  
  const { data, error } = await query;
  
  if (error) {
    console.error('Erreur lors de la récupération des coupons:', error);
    throw error;
  }
  
  return data as Coupon[];
};

/**
 * Récupère un coupon par son ID
 */
export const getCouponById = async (id: string): Promise<Coupon | null> => {
  let query = supabase
    .from('coupons')
    .select('*')
    .eq('id', id);
  
  query = applyDomainFilterToQuery(query);
  
  const { data, error } = await query.maybeSingle();
  
  if (error) {
    console.error('Erreur lors de la récupération du coupon:', error);
    throw error;
  }
  
  return data as Coupon | null;
};

/**
 * Récupère un coupon par son code
 */
export const getCouponByCode = async (code: string): Promise<Coupon | null> => {
  let query = supabase
    .from('coupons')
    .select('*')
    .eq('code', code.toUpperCase().trim());
  
  query = applyDomainFilterToQuery(query);
  
  const { data, error } = await query.maybeSingle();
  
  if (error) {
    console.error('Erreur lors de la récupération du coupon:', error);
    throw error;
  }
  
  return data as Coupon | null;
};

/**
 * Crée un nouveau coupon
 */
export const createCoupon = async (
  couponData: Omit<Coupon, 'id' | 'usage_count' | 'created_at' | 'updated_at' | 'tenant_id' | 'domain'>
): Promise<Coupon> => {
  const { domain, tenantId } = getDomainFilterParams();
  const { data: { user } } = await supabase.auth.getUser();
  
  const { data, error } = await supabase
    .from('coupons')
    .insert({
      ...couponData,
      code: couponData.code.toUpperCase().trim(),
      tenant_id: tenantId,
      domain: domain,
      created_by: user?.id || null
    })
    .select()
    .single();
  
  if (error) {
    console.error('Erreur lors de la création du coupon:', error);
    throw error;
  }
  
  return data as Coupon;
};

/**
 * Met à jour un coupon
 */
export const updateCoupon = async (
  id: string,
  updates: Partial<Omit<Coupon, 'id' | 'created_at' | 'updated_at' | 'tenant_id' | 'domain'>>
): Promise<Coupon> => {
  const updateData = { ...updates };
  
  // Normaliser le code si fourni
  if (updateData.code) {
    updateData.code = updateData.code.toUpperCase().trim();
  }
  
  const { data, error } = await supabase
    .from('coupons')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();
  
  if (error) {
    console.error('Erreur lors de la mise à jour du coupon:', error);
    throw error;
  }
  
  return data as Coupon;
};

/**
 * Supprime un coupon
 */
export const deleteCoupon = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('coupons')
    .delete()
    .eq('id', id);
  
  if (error) {
    console.error('Erreur lors de la suppression du coupon:', error);
    throw error;
  }
};

/**
 * Active/désactive un coupon
 */
export const toggleCouponStatus = async (id: string, isActive: boolean): Promise<Coupon> => {
  return updateCoupon(id, { is_active: isActive });
};

// =====================================================
// FONCTIONS DE STATISTIQUES
// =====================================================

/**
 * Récupère les statistiques d'utilisation d'un coupon
 */
export const getCouponStats = async (couponId: string): Promise<{
  total_usages: number;
  total_discount_given: number;
  total_revenue_generated: number;
  usages_by_module: Record<ModuleType, number>;
}> => {
  const { data, error } = await supabase
    .from('coupon_usages')
    .select('module_type, discount_amount, final_amount')
    .eq('coupon_id', couponId);
  
  if (error) {
    console.error('Erreur lors de la récupération des stats:', error);
    throw error;
  }
  
  const stats = {
    total_usages: data.length,
    total_discount_given: 0,
    total_revenue_generated: 0,
    usages_by_module: {
      shop: 0,
      elearning: 0,
      psy: 0
    } as Record<ModuleType, number>
  };
  
  data.forEach((usage: any) => {
    stats.total_discount_given += usage.discount_amount;
    stats.total_revenue_generated += usage.final_amount;
    stats.usages_by_module[usage.module_type as ModuleType]++;
  });
  
  return stats;
};

/**
 * Récupère l'historique d'utilisation d'un coupon
 */
export const getCouponUsageHistory = async (
  couponId: string,
  limit: number = 50
): Promise<CouponUsage[]> => {
  const { data, error } = await supabase
    .from('coupon_usages')
    .select('*')
    .eq('coupon_id', couponId)
    .order('created_at', { ascending: false })
    .limit(limit);
  
  if (error) {
    console.error('Erreur lors de la récupération de l\'historique:', error);
    throw error;
  }
  
  return data as CouponUsage[];
};

/**
 * Récupère les statistiques globales des coupons
 */
export const getGlobalCouponStats = async (): Promise<{
  total_coupons: number;
  active_coupons: number;
  total_usages: number;
  total_discount_given: number;
}> => {
  const { domain, tenantId } = getDomainFilterParams();
  
  // Compter les coupons
  let couponsQuery = supabase
    .from('coupons')
    .select('id, is_active', { count: 'exact' });
  
  couponsQuery = applyDomainFilterToQuery(couponsQuery);
  
  const { data: coupons, count: totalCoupons } = await couponsQuery;
  
  const activeCoupons = coupons?.filter((c: any) => c.is_active).length || 0;
  
  // Compter les utilisations
  let usagesQuery = supabase
    .from('coupon_usages')
    .select('discount_amount');
  
  usagesQuery = applyDomainFilterToQuery(usagesQuery);
  
  const { data: usages } = await usagesQuery;
  
  const totalUsages = usages?.length || 0;
  const totalDiscountGiven = usages?.reduce((sum: number, u: any) => sum + u.discount_amount, 0) || 0;
  
  return {
    total_coupons: totalCoupons || 0,
    active_coupons: activeCoupons,
    total_usages: totalUsages,
    total_discount_given: totalDiscountGiven
  };
};
